// roleUtils.js
//
// Role system, v2 (multi-role) — every appUsers doc is moving from a single `role` string to a
// `roles` array, so a person can hold more than one at once (e.g. a technician who also does
// crew project work sometimes). Every helper here reads `roles` when present and falls back to
// the legacy `role` string otherwise, so this works identically for an account that's already
// been migrated and one that hasn't yet (see migrateUserRoles.js for the one-time backfill
// script — this fallback means that script is safe to run whenever, not a hard cutover
// everything else depends on).
export const ROLES = {
  ADMIN: 'admin',
  COORDINATOR: 'coordinator',
  TECHNICIAN: 'technician'
};

export const ROLE_LABELS = {
  [ROLES.ADMIN]: 'Admin',
  [ROLES.COORDINATOR]: 'Coordinator',
  [ROLES.TECHNICIAN]: 'Technician'
};

// Always returns a non-empty array. Mirrors firestore.rules' hasRole() fallback exactly — see
// that function's own comment for why both fields are checked rather than requiring migration
// to have already run.
export const getUserRoles = (userData) => {
  if (!userData) return [ROLES.TECHNICIAN];
  if (Array.isArray(userData.roles) && userData.roles.length > 0) return userData.roles;
  if (userData.role) return [userData.role];
  return [ROLES.TECHNICIAN];
};

export const userHasRole = (userData, roleName) => getUserRoles(userData).includes(roleName);

export const isAdminRoles = (roles) => Array.isArray(roles) && roles.includes(ROLES.ADMIN);
export const isCoordinatorRoles = (roles) => Array.isArray(roles) && roles.includes(ROLES.COORDINATOR);
export const isTechnicianRoles = (roles) => Array.isArray(roles) && roles.includes(ROLES.TECHNICIAN);

// Precedence for anything that genuinely needs one single value out of a multi-role account —
// the legacy `role` string field (kept in sync on every write for defense-in-depth against any
// read site this migration missed) and App.js's dashboard-shell selection. Office-capable roles
// win over field roles: someone who's both admin and technician (a working owner) still lands
// in the full office Dashboard, which is a superset of what the field apps offer.
const SHELL_PRECEDENCE = [ROLES.ADMIN, ROLES.COORDINATOR, ROLES.TECHNICIAN];
export const primaryRole = (roles) => {
  if (!Array.isArray(roles) || roles.length === 0) return ROLES.TECHNICIAN;
  return SHELL_PRECEDENCE.find((r) => roles.includes(r)) || roles[0];
};

// The one place App.js needs to decide which shell to mount. 'office' = Dashboard (admin or
// coordinator — same shell, SettingsHub.js itself narrows what a coordinator sees inside it).
// 'field' = TechDashboard.
export const shellForRoles = (roles) => {
  if (isAdminRoles(roles) || isCoordinatorRoles(roles)) return 'office';
  return 'field';
};
